import axios from 'axios';

export interface DispatchSettingsResponse {
  dispatch_runtime_minutes?: number;
  revert_to_draft_on_no_available?: boolean;
}

export interface SmartDispatchRequest {
  workOrders: Array<{
    id: number;
    settings: Record<string, unknown>;
  }>;
}

export interface DispatchCriterion {
  criteriaType: string;
  appliesToSegments?: string[];
  [key: string]: unknown;
}

export interface DispatchExpansionSettingsResponse {
  _included?: {
    criteria?: DispatchCriterion[];
    expansionSettings?: {
      marketplace?: { enabled: boolean };
      serviceTerritory?: { enabled: boolean };
      talentPools?: { enabled: boolean };
    };
  };
}

async function makeApiRequest<T>(
  url: string,
  options: Record<string, unknown> = {},
): Promise<T> {
  try {
    const response = await axios({
      url,
      timeout: 10000,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      ...options,
    });

    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
}

/**
 * Fetches dispatch settings for a specific project
 */
export const getDispatchSettings = async (
  projectId: string | number,
): Promise<DispatchSettingsResponse> => makeApiRequest<DispatchSettingsResponse>(
  `/dispatch-automation/v1/projects/${projectId}/dispatch-settings`,
  { method: 'GET' },
);

/**
 * Fetches V2 dispatch expansion settings (service territory, marketplace, talent pools)
 * and provider eligibility criteria for a project. Segments are read from
 * `_included.expansionSettings`; criteria (each carrying a `criteriaType` enum) from
 * `_included.criteria`.
 */
export const getDispatchExpansionSettings = async (
  projectId: string | number,
): Promise<DispatchExpansionSettingsResponse> => makeApiRequest<DispatchExpansionSettingsResponse>(
  `/dispatch-automation/v2/projects/${projectId}/dispatch-settings?returnLatestRow=1&include=expansionSettings,criteria`,
  { method: 'GET' },
);
